import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { HttpUtilService } from '../../../services/http-util.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { User } from '../../user/model/user.model';
import { Company } from 'src/app/models/company';
import { LocalCompany } from 'src/app/models/LocalCompany';
import { PersonnelDto } from 'src/app/models/personnel';

@Injectable({
  providedIn: 'root',
})
export class AdminService {
  public patientId: BehaviorSubject<any> = new BehaviorSubject('');
  public ticketNumber: BehaviorSubject<any> = new BehaviorSubject('');
  public selectedSpecialty: BehaviorSubject<string> = new BehaviorSubject('');
  public passwordHash = new BehaviorSubject<string>(undefined);

  constructor(
    private httpUtilService: HttpUtilService,
    private httpClient: HttpClient
  ) {}

  getPermissions() {
    const self = this;
    const apiEndpoint = 'permissions/list';

    return self.httpUtilService.request<any[]>('GET', null, apiEndpoint)
  }

  getPermissionsByRole() {
    const self = this;
    const apiEndpoint = 'permissions/by-roles';

    return self.httpUtilService.request<any>('GET', null, apiEndpoint)
  }

  assignPermissionToRole(roleId: number, permissionCode: string): Observable<HttpResponse<any>> {
    return this.httpUtilService.request('POST', { roleId, permissionCode }, 'permissions/assign');
  }

  revokePermissionFromRole(roleId: number, permissionCode: string): Observable<HttpResponse<any>> {
    return this.httpUtilService.request('POST', { roleId, permissionCode }, 'permissions/revoke');
  }

  getNonApprovedMedics() {
    const self = this;
    const apiEndpoint = 'users/not-approved-medics';

    return self.httpUtilService.invokeQuery('GET', null, apiEndpoint);
  }

  approveMedic(id: number) {
    const self = this;
    const apiEndpoint = 'users/approve-medic';
    const params = {
      key: 'id',
      value: id,
    };
    return self.httpUtilService.invokeQuery('GET', params, apiEndpoint);
  }

  denyMedic(id: number) {
    const self = this;
    const apiEndpoint = 'users/deny-medic';
    const params = {
      key: 'id',
      value: id,
    };
    return self.httpUtilService.invokeQuery('GET', params, apiEndpoint);
  }

  disAffiliationCompany(companyId: number, medicId: number) {
    const self = this;
    const apiEndpoint = `company/${companyId}/personnel/${medicId}/disaffiliate`;
    return self.httpUtilService.request('POST', null, apiEndpoint);
  }

  freezeUser(companyId: number, userId: number, userType:string) {
    const self = this;
    const apiEndpoint = `company/${companyId}/personnel/${userId}/toggleFreeze`;
    return self.httpUtilService.request('POST', null, apiEndpoint);
  }

  batchFreezeUsers(companyId: number, userIds: number[], userType: string) {
    const self = this;
    const apiEndpoint = (userType === 'M' ? 'users/batch-freeze' : `company/${companyId}/personnel/batch/toggleFreeze`);
    return self.httpUtilService.invoke('POST', userIds, apiEndpoint, '');
  }

  getLinkedCompanies(companyId: number) {
    const self = this;
    const apiEndpoint = 'company/links';
    const params = {
      key: 'companyId',
      value: companyId,
    };

    return self.httpUtilService.invokeQuery('GET', params, apiEndpoint);
  }

  linkCompanies(parentCompanyId: number, childCompanyIds: number[]) {
    const self = this;
    const apiEndpoint = 'company/links';
    return self.httpUtilService.invoke('POST', { parentCompanyId, childCompanyIds }, apiEndpoint, '');
  }

  getActiveUserCounts():Promise<Observable<HttpResponse<Object>>> {
    const self = this;
    const apiEndpoint = 'users/active-user-counts';
    return self.httpUtilService.invokeQuery('GET', null, apiEndpoint);
  }

  getUserCounts():Promise<Observable<HttpResponse<Object>>> {
    const self = this;
    const apiEndpoint = 'users/all-user-counts';
    return self.httpUtilService.invokeQuery('GET', null, apiEndpoint);
  }
  
  getListCompany():Promise<Observable<HttpResponse<Object>>> {
    const self = this;
    const apiEndpoint = 'company/list';
    return self.httpUtilService.invokeQuery('GET', null, apiEndpoint);
  }

  getListCompanyOperations(startDate:string, endDate: string):Promise<Observable<HttpResponse<Object>>> {
    const self = this;
    const apiEndpoint = 'company/operations';

    let params = [];

    if (startDate) {
      params.push({   
        key: 'startDate',
        value: startDate?.toString()
      })
    };
      
    if (endDate) {
        params.push({   
          key: 'endDate',
          value: endDate?.toString()
        })
    };

    return self.httpUtilService.invokeQueryAnyParams('GET', params, apiEndpoint);

  }
  
  generateadmingeneral(companyId:number, user: User):Promise<Observable<HttpResponse<Object>>> {
    const self = this;
    const apiEndpoint = 'company/' + companyId + '/admin';
    // console.log(user, " ", apiEndpoint);
    return self.httpUtilService.invoke('POST', user, apiEndpoint,'');
  }

  disableTwoFactorAuth():Promise<Observable<HttpResponse<Object>>> {
    const self = this;
    const apiEndpoint = 'admin/disableTwoFactor';
    return self.httpUtilService.invoke('POST', null, apiEndpoint,'');
  }

  disableOpenData():Promise<Observable<HttpResponse<Object>>> {
    const self = this;
    const apiEndpoint = 'admin/disableOpenData';
    return self.httpUtilService.invoke('POST', null, apiEndpoint,'');
  }


  seedUserRoles():Promise<Observable<HttpResponse<Object>>> {
    const self = this;
    const apiEndpoint = 'seed/generate-user-roles';
    return self.httpUtilService.invoke('POST', null, apiEndpoint,'');
  }

  seedSymptomResources():Observable<HttpResponse<Object>> {
    const self = this;
    const apiEndpoint = 'seed/seedSymptoms';
    return self.httpUtilService.request('POST', null, apiEndpoint);
  }

  createCompanyPersonnelAccount(personnel: PersonnelDto):Promise<Observable<HttpResponse<Object>>> {
    const self = this;
    const apiEndpoint = 'company/personnel/register';
    return self.httpUtilService.invoke('POST', personnel, apiEndpoint,'');
  }

  acceptInvitationAsExistingUser(invitationToken: string): Observable<HttpResponse<any>> {
    const apiEndpoint = 'company/personnel/accept-invitation';
    return this.httpUtilService.request('POST', { InvitationToken: invitationToken }, apiEndpoint);
  }

  createCompany(company: Company):Promise<Observable<HttpResponse<Object>>> {
    const self = this;
    const apiEndpoint = 'company';
    return self.httpUtilService.invoke('POST', company, apiEndpoint,'');
  }

  createlocal(companyId: number, localCompany: LocalCompany):Promise<Observable<HttpResponse<Object>>> {
    const self = this;
    const apiEndpoint = 'company/' + companyId + '/locals';
    return self.httpUtilService.invoke('POST', localCompany, apiEndpoint,'');
  }

  deletelocal(companyId: number, localId:number):Promise<Observable<HttpResponse<Object>>> {
    const self = this;
    const apiEndpoint = 'company/' + companyId + '/locals/' + localId;
    return self.httpUtilService.invoke('DELETE', null, apiEndpoint,'');
  }

  getListLocal(companyId: number):Promise<Observable<HttpResponse<Object>>> {
    const self = this;
    const apiEndpoint = 'company/' + companyId + '/locals';
    return self.httpUtilService.invoke('GET', null, apiEndpoint,'');
  }

  getAdminCompany(companyId:number):Promise<Observable<HttpResponse<Object>>> {
    const self = this;
    const apiEndpoint = 'company/' + companyId + '/admin';
    return self.httpUtilService.invokeQuery('GET', null, apiEndpoint);
  }

  getDataCompany(companyId:number):Promise<Observable<HttpResponse<Object>>> {
    const self = this;
    const apiEndpoint = 'company/' + companyId + '/data';
    return self.httpUtilService.invokeQuery('GET', null, apiEndpoint);
  }

  getDataCompanyByRuc(ruc:string):Promise<Observable<HttpResponse<Object>>> {
    const self = this;
    const apiEndpoint = 'company/byRuc/' + ruc;
    return self.httpUtilService.invokeQuery('GET', null, apiEndpoint);
  }

  getDataLocal(companyId:number, localId:number):Promise<Observable<HttpResponse<Object>>> {
    const self = this;
    const apiEndpoint = 'company/' + companyId + '/locals/' + localId;
    return self.httpUtilService.invokeQuery('GET', null, apiEndpoint);
  }

  getDashboardAdminGeneral(startDate:string, endDate: string):Promise<Observable<HttpResponse<Object>>> {
    const self = this;
    const apiEndpoint = 'admin/dashboard';

    let params = [];

    if (startDate) {
      params.push({   
        key: 'startDate',
        value: startDate?.toString()
      })
    };
      
    if (endDate) {
        params.push({   
          key: 'endDate',
          value: endDate?.toString()
        })
    };

    return self.httpUtilService.invokeQueryAnyParams('GET', params, apiEndpoint);
  }

  getDashBoard(companyId:number, localId:number, startDate:string, endDate: string):Promise<Observable<HttpResponse<Object>>> {
    const self = this;
    const apiEndpoint = 'company/' + companyId + '/dashboard';

    let params = [];

    params.push({
      key: 'localId',
      value: localId?.toString()
    });

    if (startDate) {
      params.push({
        key: 'startDate',
        value: startDate?.toString()
      })
    };

    if (endDate) {
        params.push({
          key: 'endDate',
          value: endDate?.toString()
        })
    };
    return self.httpUtilService.invokeQueryAnyParams('GET', params, apiEndpoint);
  }

  getAttentionsReport(companyId:number, localId:number, startDate:string, endDate: string):Promise<Observable<HttpResponse<Object>>> {
    const self = this;
    const apiEndpoint = 'company/' + companyId + '/attentionsReport';
    let params = [];

    params.push({
      key: 'localId',
      value: localId?.toString()
    });

    if (startDate) {
      params.push({
        key: 'startDate',
        value: startDate?.toString()
      })
    };

    if (endDate) {
        params.push({
          key: 'endDate',
          value: endDate?.toString()
        })
    };
    return self.httpUtilService.invokeQueryAnyParams('GET', params, apiEndpoint);
  }

  getPatientsAttendedReport(companyId: number, localId?: number, startDate?: string, endDate?: string):Observable<HttpResponse<Object>>{
      const queryParams: { [key: string]: any } = {};

      if (startDate) queryParams['startDate'] = startDate;
      if (endDate) queryParams['endDate'] = endDate;
      if (localId != null) queryParams['localId'] = localId;

      const url = `company/${companyId}/patientsAttendedReport`;
      
      return this.httpUtilService
        .request<any[]>('GET', queryParams, url)
  };
  
  getPersonalCompany(companyId:number, localId:number):Promise<Observable<HttpResponse<Object>>> {
    const self = this;
    const apiEndpoint = 'company/' + companyId + '/personnel/legacy/' + localId;
    return self.httpUtilService.invokeQuery('GET', null, apiEndpoint);
  }

  getInfoDetailCompany(companyId:number):Promise<Observable<HttpResponse<Object>>> {
    const self = this;
    const apiEndpoint = 'company/' + companyId + '/details';
    return self.httpUtilService.invokeQuery('GET', null, apiEndpoint);
  }

  sendInvitationToDoctorAffilation(payload:any):Promise<Observable<HttpResponse<Object>>> {
    const self = this;
    const apiEndpoint = 'users/sendinvitationtodoctoraffilation';
    return self.httpUtilService.invoke('POST', payload, apiEndpoint,'');
  }

  createInvitationDoctor(companyId: number, payload:any):Observable<HttpResponse<Object>> {
    const self = this;
    const apiEndpoint = `company/${companyId}/personnel/invitations`;
    return self.httpUtilService.request('POST', payload, apiEndpoint);
  }

  deleteInvitationDoctor(companyId: number, payload:any):Promise<Observable<HttpResponse<Object>>> {
    const self = this;
    const apiEndpoint = `company/${companyId}/personnel/invitations`;
    return self.httpUtilService.invoke('DELETE', payload, apiEndpoint,'');
  }

  getListRoles(companyId: number):Promise<Observable<HttpResponse<Object>>> {
    const self = this;
    const apiEndpoint = `company/${companyId}/personnel/allowedRoles`;
    return self.httpUtilService.invokeQuery('GET', null, apiEndpoint);
  }

  createApiKey(companyId: number, credentials: any):Promise<Observable<HttpResponse<Object>>> {
    const self = this;
    const apiEndpoint = 'company/' + companyId + '/apiKeys';
    return self.httpUtilService.invoke('POST', credentials, apiEndpoint, '');
  }

  renewApiKey(companyId: number, apiKeyId: number): Observable<HttpResponse<Object>>{
    const apiEndpoint = `company/${companyId.toString()}/apiKeys/${apiKeyId.toString()}/renew`;
    return this.httpUtilService.request('POST', null, apiEndpoint);
  }

  deleteApiKey(companyId: number, apiKey: string): Promise<Observable<HttpResponse<Object>>> {
    const self = this;
    const apiEndpoint = `company/${companyId}/apiKeys/${apiKey}`;
    return self.httpUtilService.invokeQuery('DELETE', null, apiEndpoint);
  }

  getAllowedCompanyApiKeyCategories(companyId: number):Observable<HttpResponse<Object>> {
    const self = this;
    const apiEndpoint = 'company/' + companyId + '/apiKeys/allowed';
    return self.httpUtilService.request('GET', null, apiEndpoint);
  }

  getCompanyApiKeys(companyId: number):Observable<HttpResponse<Object>> {
    const self = this;
    const apiEndpoint = 'company/' + companyId + '/apiKeys';
    return self.httpUtilService.request('GET', null, apiEndpoint);
  }

  uploadCompanyLogo(companyId: number, file: File): Promise<Observable<HttpResponse<Object>>> {
    const self = this;
    const apiEndpoint = 'company/' + companyId + '/logo';

    const formData = new FormData();
    formData.append('file', file);

    return self.httpUtilService.invokeWithFormData('POST', formData, apiEndpoint);
    // return this.http.post(`${this.baseUrl}/logo/upload/${companyId}`, formData);
  }


  verifyDiagnosisUpload(diagnoses:any[]):Promise<Observable<HttpResponse<Object>>> {
    const self = this;
    const apiEndpoint = 'diagnosis/verify';

    return self.httpUtilService.invokeWithFormData('POST', diagnoses, apiEndpoint);
  }

  batchUploadDiagnoses(diagnoses:any[]):Promise<Observable<HttpResponse<Object>>> {
    const self = this;
    const apiEndpoint = 'diagnosis/batchUpload';

    return self.httpUtilService.invokeWithFormData('POST', diagnoses, apiEndpoint);
  }

  generateCompanyPatientDataAccesses(): Promise<Observable<HttpResponse<Object>>> {
    const self = this;
    const apiEndpoint = 'seed/generate-company-patient-access';

    return self.httpUtilService.invokeWithFormData('POST', null, apiEndpoint);
  }

  migrateMedicalCenter(payload: any): Observable<HttpResponse<any>> {
    return this.httpUtilService.request('POST', payload, 'admin/companyMigration/medicalCenter');
  }

  migratePharmacy(payload: any): Observable<HttpResponse<any>> {
    return this.httpUtilService.request('POST', payload, 'admin/companyMigration/pharmacy');
  }

  migrateDiagnostics(payload: any): Observable<HttpResponse<any>> {
    return this.httpUtilService.request('POST', payload, 'admin/companyMigration/diagnostics');
  }

}
